// Printing a submission through a template: the page the builder's preview
// draws with sample answers, drawn into a PDF with the submission's own.
//
// The preview (TemplateBox) is the reference, and everything here follows the
// CSS it is set in. A box is a border-box frame: its border runs inside its
// edge, 2 pt of padding sits inside the border, and whatever runs past the
// padding is clipped. Type is Helvetica at a line height of 1.2. One-line
// formats are centred top to bottom and cut short with an ellipsis; notes and
// typed text wrap from the top. Answers are formatted by templateValues, the
// same functions the preview formats its samples with.
//
// jsPDF works in the layout's own units — points from the top-left corner of
// the sheet — so a box goes onto the page where it is stored.
import { submittedDay } from '../dateFormat';
import { downloadBlob, sanitizeFilename } from '../downloadFile';
import { getSchemaFields } from '../formSections';
import { resolvePhotoDataUrl } from '../photoStorage';
import { textWidth, toWinAnsi } from './helvetica';
import { MARK_GRID, MARK_STROKE_WIDTH, markStrokesFor } from './icons';
import { PAGE_HEIGHT, PAGE_WIDTH, isBlankBox, normalizeLayout } from './templateLayout';
import { boxText, isBoxChecked } from './templateValues';

const LINE_HEIGHT = 1.2;
// Where the baseline sits in a line: browsers centre the font's ascent and
// descent in the line's height. For Helvetica, Chrome and Safari take the
// ascent as 0.92 em — the font's own 0.77 plus the 15% they add so it spaces
// like Arial, which comes to much the same — and the descent as 0.23, which
// leaves 0.025 em of leading on top and the baseline 0.945 em down the line.
const ASCENT = 0.92;
const DESCENT = 0.23;
const BASELINE = (LINE_HEIGHT - ASCENT - DESCENT) / 2 + ASCENT;
const PADDING = 2;
const RULE = 0.75;
// TemplateBox's ink, #111827.
const INK = [17, 24, 39];
// The preview draws the mark across 80% of the box's content.
const MARK_SIZE = 0.8;
const ELLIPSIS = '…';
// Room for the rounding in a sum of widths.
const EPSILON = 1e-6;

const BLANK = { kind: 'blank' };

// --- What a box prints --------------------------------------------------------

// What detail boxes print: the submission's own title, date, submitter and form.
export const submissionDetails = (submission, form) => ({
  title: submission.title || form?.name || '',
  submitted_at: submittedDay(submission.submitted_at, submission.submitted_tz),
  submitter: submission.user_email || '',
  form_name: form?.name || submission.form_name || '',
});

// A calculation reads as the form works it out now, falling back to the
// result stored with the submission, as it does in the Excel export.
const answerFor = (field, { formData, calculatedValues }) => {
  if (field.type === 'calculation') {
    const calculated = calculatedValues?.[field.id];
    if (calculated !== undefined && calculated !== '') return calculated;
  }
  return formData?.[field.id];
};

// What one box prints for a submission, deciding what TemplateBox's preview
// decides about a sample answer:
//
//   { kind: 'blank' }                  the frame and nothing in it
//   { kind: 'text', text, wraps }      the label, if any, and then the value
//   { kind: 'check', ticked }
//   { kind: 'photo', value }           the stored photo, fetched to draw it
//
// `source` holds the form's questions by id (`fieldsById`), the answers
// (`formData`), `calculatedValues` and `hiddenIds` as useResolvedForm works
// them out, and the submission's `details`. A question whose conditions hide
// it prints blank, the way the Excel export leaves it out; so does a box whose
// question has gone from the form.
export const boxContent = (box, source) => {
  if (isBlankBox(box)) return BLANK;
  let field = null;
  let value;
  if (box.kind === 'field') {
    field = source.fieldsById.get(box.fieldId);
    if (!field || source.hiddenIds.has(field.id)) return BLANK;
    value = answerFor(field, source);
  } else if (box.kind === 'detail') {
    value = source.details[box.detail];
  }

  if (box.format === 'photo') return { kind: 'photo', value };
  if (box.format === 'check') return { kind: 'check', ticked: isBoxChecked(box, value) };
  if (box.kind === 'text') return box.text ? { kind: 'text', text: box.text, wraps: true } : BLANK;
  const label = (box.label || '').trim();
  const text = boxText(box, field, value);
  return { kind: 'text', text: label ? `${label} ${text}` : text, wraps: box.format === 'note' };
};

// --- Where it goes -------------------------------------------------------------

// The two rectangles that matter inside a box: `clip`, inside its border, past
// which nothing shows, and `content`, inside the padding, where things are set.
export const boxFrame = (box) => {
  const side = box.border === 'box' ? RULE : 0;
  const bottom = box.border === 'box' || box.border === 'underline' ? RULE : 0;
  const clip = {
    x: box.x + side,
    y: box.y + side,
    w: Math.max(0, box.w - 2 * side),
    h: Math.max(0, box.h - side - bottom),
  };
  const content = {
    x: clip.x + PADDING,
    y: clip.y + PADDING,
    w: Math.max(0, clip.w - 2 * PADDING),
    h: Math.max(0, clip.h - 2 * PADDING),
  };
  return { clip, content };
};

const trimTrailingSpaces = (line) => line.replace(/ +$/, '');

// White space as CSS collapses it outside `pre`: each run down to one space,
// none at either end.
const collapseSpace = (text) => text.replace(/[ \t\n\r\f]+/g, ' ').replace(/^ | $/g, '');

// One line set the way the preview's `truncate` sets it: white space
// collapsed, and cut short with an ellipsis when it runs past `width`. `cut`
// says whether it was.
export const fitLine = (text, width, measure) => {
  const line = collapseSpace(text);
  if (measure(line) <= width + EPSILON) return { text: line, cut: false };
  const room = width - measure(ELLIPSIS);
  let kept = '';
  let used = 0;
  for (const char of line) {
    used += measure(char);
    if (used > room + EPSILON) break;
    kept += char;
  }
  return { text: kept + ELLIPSIS, cut: true };
};

const DASHES = '–—';

// A paragraph cut at the places a line may break: after a run of spaces, after
// a hyphen (though not one before a digit, which is more likely a minus or a
// range) and after a dash, and before an em dash.
const breakUnits = (paragraph) => {
  const chars = Array.from(paragraph);
  const units = [];
  let unit = '';
  chars.forEach((char, index) => {
    const next = chars[index + 1];
    unit += char;
    if (next === undefined || next === ' ') return;
    const breaksAfter = char === ' '
      || (char === '-' && !/\d/.test(next))
      || DASHES.includes(char)
      || next === '—';
    if (breaksAfter) {
      units.push(unit);
      unit = '';
    }
  });
  if (unit) units.push(unit);
  return units;
};

// Lines set the way the preview's `whitespace-pre-wrap break-words` sets them:
// the author's own line breaks kept, and spaces kept; a line broken at the last
// place it may break before it runs past `width`, its trailing spaces left
// hanging past the edge; and a word too long for a line of its own broken
// wherever it has to be.
export const wrapLines = (text, width, measure) => {
  const fits = (line) => measure(trimTrailingSpaces(line)) <= width + EPSILON;
  const lines = [];
  text.replace(/\r\n?|\f/g, '\n').replace(/\t/g, ' ').split('\n').forEach((paragraph) => {
    let line = '';
    breakUnits(paragraph).forEach((unit) => {
      if (fits(line + unit)) {
        line += unit;
        return;
      }
      if (line) lines.push(line);
      line = '';
      for (const char of unit) {
        if (line && char !== ' ' && !fits(line + char)) {
          lines.push(line);
          line = '';
        }
        line += char;
      }
    });
    lines.push(line);
  });
  return lines.map(trimTrailingSpaces);
};

// The lines a box's text prints as, each with where its baseline starts: one
// line centred top to bottom, or wrapped lines from the top. Lines that would
// start below the box are left off, since the clip would hide them anyway.
// `text` must already be in WinAnsi (see toWinAnsi).
export const textLines = (box, text, wraps) => {
  const { clip, content } = boxFrame(box);
  const measure = (line) => textWidth(line, box.fontSize, box.bold);
  const lineHeight = box.fontSize * LINE_HEIGHT;
  const fitted = wraps
    ? wrapLines(text, content.w, measure).map((line) => ({ text: line, cut: false }))
    : [fitLine(text, content.w, measure)];
  const top = wraps ? content.y : content.y + (content.h - lineHeight) / 2;

  return fitted
    .map((line, index) => {
      const width = measure(line.text);
      let x = content.x;
      // A line too long for the box starts at its left, whatever the alignment.
      if (!line.cut && width <= content.w + EPSILON) {
        if (box.align === 'center') x += (content.w - width) / 2;
        if (box.align === 'right') x += content.w - width;
      }
      const lineTop = top + index * lineHeight;
      return { text: line.text, x, y: lineTop + box.fontSize * BASELINE, lineTop };
    })
    .filter((line) => line.text && line.lineTop < clip.y + clip.h)
    .map(({ text: lineText, x, y }) => ({ text: lineText, x, y }));
};

// The checkmark's strokes, as points on the page, and how thick they're
// drawn: the preview's 20-unit mark scaled to 80% of the box's content and
// centred in it.
export const markStrokes = (box) => {
  const { content } = boxFrame(box);
  const scale = (Math.min(content.w, content.h) * MARK_SIZE) / MARK_GRID;
  const centreX = content.x + content.w / 2;
  const centreY = content.y + content.h / 2;
  const strokes = markStrokesFor(box.mark).map((points) =>
    points.map(([x, y]) => [centreX + (x - MARK_GRID / 2) * scale, centreY + (y - MARK_GRID / 2) * scale]));
  return { strokes, width: MARK_STROKE_WIDTH * scale };
};

// A picture `width` by `height` scaled to fit whole inside `rect`, centred in
// it — the way `object-fit: contain` fits one.
export const fitImage = (width, height, rect) => {
  const scale = Math.min(rect.w / width, rect.h / height);
  const w = width * scale;
  const h = height * scale;
  return { x: rect.x + (rect.w - w) / 2, y: rect.y + (rect.h - h) / 2, w, h };
};

// --- Drawing it ---------------------------------------------------------------

const fontStyle = ({ bold, italic }) => {
  if (bold) return italic ? 'bolditalic' : 'bold';
  return italic ? 'italic' : 'normal';
};

const withClip = (doc, rect, draw) => {
  doc.saveGraphicsState();
  doc.rect(rect.x, rect.y, rect.w, rect.h, null);
  doc.clip();
  doc.discardPath();
  draw();
  doc.restoreGraphicsState();
};

// The border is stroked half its width in from the edge, so it lies inside
// the box the way a CSS border does.
const drawBorder = (doc, box) => {
  if (box.border !== 'box' && box.border !== 'underline') return;
  const half = RULE / 2;
  doc.setLineWidth(RULE);
  doc.setLineCap('butt');
  doc.setLineJoin('miter');
  if (box.border === 'box') {
    doc.rect(box.x + half, box.y + half, box.w - RULE, box.h - RULE, 'S');
  } else {
    doc.line(box.x, box.y + box.h - half, box.x + box.w, box.y + box.h - half);
  }
};

const drawStroke = (doc, points) => {
  const [[startX, startY], ...rest] = points;
  let x = startX;
  let y = startY;
  const moves = rest.map(([toX, toY]) => {
    const move = [toX - x, toY - y];
    x = toX;
    y = toY;
    return move;
  });
  doc.lines(moves, startX, startY, [1, 1], 'S', false);
};

const drawBox = (doc, box, content, photos) => {
  drawBorder(doc, box);
  const { clip, content: inside } = boxFrame(box);

  if (content.kind === 'text') {
    const lines = textLines(box, toWinAnsi(content.text), content.wraps);
    if (!lines.length) return;
    withClip(doc, clip, () => {
      doc.setFont('helvetica', fontStyle(box));
      doc.setFontSize(box.fontSize);
      lines.forEach((line) => doc.text(line.text, line.x, line.y));
    });
  } else if (content.kind === 'check' && content.ticked) {
    const { strokes, width } = markStrokes(box);
    withClip(doc, clip, () => {
      doc.setLineWidth(width);
      doc.setLineCap('round');
      doc.setLineJoin('round');
      strokes.forEach((points) => drawStroke(doc, points));
    });
  } else if (content.kind === 'photo') {
    const photo = photos.get(content.value);
    if (!photo) return;
    const rect = fitImage(photo.width, photo.height, inside);
    withClip(doc, clip, () => {
      doc.addImage(photo.dataUrl, photo.format, rect.x, rect.y, rect.w, rect.h, photo.alias);
    });
  }
};

// Every photo the page prints, fetched once however many boxes show it. One
// that can't be fetched or read leaves its boxes blank rather than losing
// the page.
const loadPhotos = async (doc, contents) => {
  const values = [...new Set(
    contents.filter((content) => content.kind === 'photo' && content.value).map((content) => content.value),
  )];
  const photos = new Map();
  await Promise.all(values.map(async (value, index) => {
    try {
      const dataUrl = await resolvePhotoDataUrl(value);
      if (!dataUrl) return;
      const { width, height, fileType } = doc.getImageProperties(dataUrl);
      if (width > 0 && height > 0) {
        photos.set(value, { dataUrl, width, height, format: fileType, alias: `photo-${index}` });
      }
    } catch (error) {
      console.error('Failed to load a photo for the PDF:', error);
    }
  }));
  return photos;
};

// One submission printed through one template, as a jsPDF document. The
// arguments are what SubmissionView has on hand: the form's `schema`, the
// answers (`formData`), useResolvedForm's `calculatedValues` and `hiddenIds`,
// and the `submission` and `form` records.
export const buildTemplatePdf = async ({
  template,
  schema,
  formData,
  calculatedValues,
  hiddenIds = new Set(),
  submission,
  form,
}) => {
  // jsPDF is only fetched once somebody asks for a PDF.
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: [PAGE_WIDTH, PAGE_HEIGHT], orientation: 'portrait' });
  doc.setProperties({ title: submission.title || form?.name || template.name });

  const layout = normalizeLayout(template.layout);
  const source = {
    fieldsById: new Map(getSchemaFields(schema).map((field) => [field.id, field])),
    formData,
    calculatedValues,
    hiddenIds,
    details: submissionDetails(submission, form),
  };
  const contents = layout.boxes.map((box) => boxContent(box, source));
  const photos = await loadPhotos(doc, contents);

  doc.setTextColor(...INK);
  doc.setDrawColor(...INK);
  layout.boxes.forEach((box, index) => drawBox(doc, box, contents[index], photos));
  return doc;
};

// Build the PDF and hand it to the browser as a download.
export const downloadTemplatePdf = async (options) => {
  const doc = await buildTemplatePdf(options);
  const { submission, form, template } = options;
  const name = sanitizeFilename(submission.title || form?.name);
  downloadBlob(doc.output('blob'), `${name}-${submission.id}-${sanitizeFilename(template.name, 'template')}.pdf`);
};
